import { createContext, useContext, useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'

import { useApi } from '../lib/api.ts'
import { gbp, setCurrency } from '../lib/format.ts'
import { useSession } from '../lib/session.tsx'
import { NAV } from './nav.tsx'
import { useBetSlip } from '../lib/betslip.tsx'
import type { AppContext as Ctx } from '../../shared/types.ts'

/**
 * The persistent global frame — `app-shell.js` as a layout component.
 */

const AppCtx = createContext<Ctx | null>(null)
const TitleCtx = createContext<(title: string) => void>(() => {})

/** The global chrome's state. Null until the first poll returns. */
export function useAppContext(): Ctx | null {
  return useContext(AppCtx)
}

/** Declare the topbar title (and document title) for a page. */
export function usePageTitle(title: string) {
  const setTitle = useContext(TitleCtx)
  useEffect(() => {
    setTitle(title)
    document.title = `${title} — KellyStake`
  }, [setTitle, title])
}

type Theme = 'dark' | 'light'

function readTheme(): Theme {
  try {
    return (localStorage.getItem('ks-theme') as Theme) || 'dark'
  } catch {
    return 'dark'
  }
}

function Sun() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19" />
    </svg>
  )
}

function Moon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M21 12.8A8 8 0 1111 3a6 6 0 0010 9.8z" />
    </svg>
  )
}

export function AppShell() {
  // 5s: fast enough that mode and trading state stay current, slow enough not to thrash the DB.
  const { data: ctx } = useApi<Ctx>('/context', 5000)
  const [theme, setTheme] = useState<Theme>(readTheme)
  const [navOpen, setNavOpen] = useState(false)
  const slip = useBetSlip()
  const [title, setTitle] = useState('')
  const location = useLocation()
  // Set during render, before the page below renders, so its first paint is in the right currency.
  if (ctx) setCurrency(ctx.round.currency)

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    try {
      localStorage.setItem('ks-theme', theme)
    } catch {
      /* private mode — the theme simply won't persist */
    }
  }, [theme])

  useEffect(() => {
    document.body.classList.toggle('nav-open', navOpen)
  }, [navOpen])

  useEffect(() => setNavOpen(false), [location.pathname])

  return (
    <AppCtx.Provider value={ctx}>
      <TitleCtx.Provider value={setTitle}>
        <aside className="sidebar">
          <div className="brand">
            <span className="brand-mark">K</span>
            <span>KellyStake</span>
          </div>
          <nav className="nav">
            {NAV.map((group) => (
              <div className={`nav-section${group.bottom ? ' bottom' : ''}`} key={group.section}>
                <div className="nav-section-label">{group.section}</div>
                {group.items.map((item) => (
                  <NavLink
                    key={item.key}
                    to={item.path}
                    end={item.path === '/'}
                    className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                    {item.key === 'betslip' && slip.selections.length > 0 && (
                      <span className="ni-badge" aria-label={`${slip.selections.length} on the slip`}>
                        {slip.selections.length}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
        </aside>

        {navOpen && <div className="nav-scrim" onClick={() => setNavOpen(false)} />}

        <header className="topbar">
          <button
            className="icon-btn nav-toggle"
            aria-label="Menu"
            aria-expanded={navOpen}
            onClick={() => setNavOpen((v) => !v)}
          >
            ☰
          </button>
          <div className="tb-title">{title}</div>
          <div className="spacer" />

          {ctx && (
            <Link
              to="/bankroll"
              className="btn ghost sm u-mono"
              style={ctx.round.status === 'bust' ? { color: 'var(--neg)' } : undefined}
              title={`Round ${ctx.round.number} · ${gbp(ctx.round.available)} available to bet`}
            >
              {ctx.round.status === 'bust' ? `Round ${ctx.round.number} · bust` : gbp(ctx.round.balance)}
            </Link>
          )}

          <button
            className="icon-btn"
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          >
            {theme === 'dark' ? <Sun /> : <Moon />}
          </button>

          <UserMenu />
        </header>

        <Outlet />
      </TitleCtx.Provider>
    </AppCtx.Provider>
  )
}

/**
 * The avatar, and the only way out.
 *
 * Signing out is a menu item rather than a button in the bar, so it is never hit by accident.
 */
function UserMenu() {
  const { user, signOut } = useSession()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.user-menu')) setOpen(false)
    }
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', esc)
    }
  }, [open])

  if (!user) return null

  return (
    <div className="user-menu">
      <button
        className="avatar"
        title={`${user.displayName} — ${user.role}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {user.initials}
      </button>

      {open && (
        <div className="user-menu-panel" role="menu">
          <div className="um-head">
            <div className="cell-strong">{user.displayName}</div>
            <div className="cell-sub">{user.email}</div>
            <div className="row" style={{ gap: 6, marginTop: 8 }}>
              <span className="badge teal">{user.role}</span>
              {!user.emailVerified && <span className="badge warn">Email unverified</span>}
            </div>
          </div>
          <Link className="um-item" role="menuitem" to="/settings/profile" onClick={() => setOpen(false)}>
            Settings
          </Link>
          <button
            className="um-item"
            role="menuitem"
            onClick={async () => {
              setOpen(false)
              await signOut()
              navigate('/login', { replace: true })
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}
